/**
 * A help chunk that did not load is "the widget is unavailable", never "this tab runs a stale build" (plan-019 E2E fix;
 * AC-HA-1, OD-24; design §31.8.9).
 *
 * The console's stale-build guard (`../stale-build.ts`, wired in `../main.tsx`) answers Vite's `vite:preloadError` by
 * reloading the page once and preventing the error — after which the failed `import()` resolves `undefined`. A content
 * blocker refusing the widget's `hp.[hash].js` files is not a deploy: reloading cannot bring them back, and the
 * `undefined` module broke the mount with a TypeError. So the guard leaves a help chunk's failure alone, the import
 * rejects, and the help loader turns it into "unavailable" (the person menu's Help opens `/help`).
 *
 * A failure is the widget's when:
 *   - the browser names the file and it is an `hp` chunk (Chromium, Firefox: "Failed to fetch dynamically imported
 *     module: https://…/assets/hp.<hash>.js"); or
 *   - the browser names no file (Safari: "Importing a module script failed.") while a help import is in flight — the
 *     launcher's ({@link loadHelpChunk} around the mount's import) or the panel's (around the widget's own `loadPanel`).
 *
 * A STALE DEPLOY IS NOT A BLOCKER (plan-019 REVIEW-FIX). A tab opened before a deploy asks for `hp` files the deploy
 * removed, exactly as a blocked one fails — and reloading DOES help it. So before a help chunk's failure is left as
 * "unavailable", {@link loadHelpChunk} asks whether the build changed: the entry script the server's `index.html` names
 * now against the one this tab runs ({@link servedBuildChanged}, one `no-store` read). Changed: the page reloads once,
 * as for any stale chunk (`../stale-build.ts`'s mark). The same build: the chunk was refused, the widget is unavailable.
 * Offline or unreadable: not known to be stale, so no reload. And because Safari names no file, an unnamed failure
 * while a help import is in flight may be ANOTHER chunk's: the guard ({@link isUnnamedChunkFailure} in `../main.tsx`)
 * runs the same check for it, so a stale route chunk still reloads.
 */
import { HELP_ASSET_STEM } from "@wetalk/help-widget/path";

import { browserStaleBuildReload, reloadOnceForStaleBuild } from "../stale-build.js";

/** A script file named in an error message: its last path segment. */
const NAMED_SCRIPT = /[^/\s"'`()]+\.m?js\b/g;
const HELP_CHUNK_FILE = new RegExp(`^${HELP_ASSET_STEM}\\.[\\w-]+\\.m?js$`);

let helpImportInFlight = 0;

/**
 * Runs a help import, marking it in flight until it settles. Vite raises `vite:preloadError` before the import's own
 * promise settles, so the guard sees the mark.
 */
export function loadHelpChunk<T>(load: () => Promise<T>, staleBuild: HelpStaleBuild = browserHelpStaleBuild()): Promise<T> {
  helpImportInFlight += 1;
  let loading: Promise<T>;
  try {
    loading = load();
  } catch (raised: unknown) {
    helpImportInFlight -= 1;
    return Promise.reject(raised instanceof Error ? raised : new Error(String(raised)));
  }
  return loading
    .finally(() => {
      helpImportInFlight -= 1;
    })
    .catch(async (raised: unknown) => {
      // A deploy replaced the build: reload as for any stale chunk. The import still rejects (the page is leaving).
      await reloadIfStaleBuild(staleBuild);
      throw raised;
    });
}

/** What telling a stale build from a refused chunk needs (the browser's own in {@link browserHelpStaleBuild}). */
export interface HelpStaleBuild {
  /** The entry script this tab runs (`<script type="module" src>`), or `null` when the page has none. */
  currentEntry(): string | null;
  /** The server's `index.html` now (never from a cache); rejects when it cannot be read. */
  fetchIndex(): Promise<string>;
  /** The console's reload-once (`../stale-build.ts`); answers whether it reloaded. */
  reloadOnce(): boolean;
}

const MODULE_SCRIPT = /<script\b[^>]*\btype=["']module["'][^>]*>/gi;
const SRC_ATTRIBUTE = /\bsrc=["']([^"']+)["']/i;

/** The first module script's `src` in an HTML text — the build's entry chunk (its name carries the build's hash). */
export function entryScriptOf(html: string): string | null {
  for (const tag of html.matchAll(MODULE_SCRIPT)) {
    const src = SRC_ATTRIBUTE.exec(tag[0]);
    if (src?.[1] !== undefined) return src[1];
  }
  return null;
}

/** Whether the server now serves another build than this tab runs. Unknown (offline, no entry) is `false`. */
export async function servedBuildChanged(staleBuild: Pick<HelpStaleBuild, "currentEntry" | "fetchIndex">): Promise<boolean> {
  let running: string | null;
  let served: string | null;
  try {
    running = staleBuild.currentEntry();
    if (running === null) return false;
    served = entryScriptOf(await staleBuild.fetchIndex());
  } catch {
    return false;
  }
  return served !== null && served !== running;
}

/** Reloads once when the build changed; answers whether it did. Never rejects. */
export async function reloadIfStaleBuild(staleBuild: HelpStaleBuild = browserHelpStaleBuild()): Promise<boolean> {
  try {
    return (await servedBuildChanged(staleBuild)) && staleBuild.reloadOnce();
  } catch {
    return false;
  }
}

/** The browser's seams: this document's entry script, `GET /` with `no-store`, and the console's reload-once. */
export function browserHelpStaleBuild(): HelpStaleBuild {
  return {
    currentEntry: () => {
      const script = globalThis.document.querySelector<HTMLScriptElement>('script[type="module"][src]');
      return script === null ? null : script.getAttribute("src");
    },
    fetchIndex: async () => {
      const answer = await globalThis.fetch(new URL("/", globalThis.location.origin), { cache: "no-store" });
      if (!answer.ok) throw new Error(`index.html answered ${String(answer.status)}`);
      return answer.text();
    },
    reloadOnce: () => reloadOnceForStaleBuild(browserStaleBuildReload()),
  };
}

/** A failed import whose message names no file (Safari): it may be any chunk's, so the build is checked for it. */
export function isUnnamedChunkFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : null;
  return message !== null && [...message.matchAll(NAMED_SCRIPT)].length === 0;
}

/** Whether a failed import is one of the help widget's chunks (see the module comment). */
export function isHelpChunkFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : null;
  if (message === null) return false;
  const named = [...message.matchAll(NAMED_SCRIPT)].map((match) => match[0]);
  if (named.length > 0) return named.some((name) => HELP_CHUNK_FILE.test(name));
  return helpImportInFlight > 0;
}
